/* ============================================================================
 * deckFinancials.ts — GENERATED from the FM-0013 KERNEL financial model.
 *
 * DO NOT EDIT BY HAND. Regenerate:
 *   cd financial_modelling/dashboard-preview
 *   ./node_modules/.bin/tsx ../scripts/gen_market_by_year.ts --write
 * Drift check (exits 1 when stale):
 *   ./node_modules/.bin/tsx ../scripts/gen_market_by_year.ts --check
 *
 * WHY THIS FILE EXISTS: the use-of-proceeds donut was hardcoded in the deck with no
 * source anywhere in the model (recorded as ungrounded in FM-0013 D-024). The split now
 * lives in assumptions.json as uop_* inputs, so the model can state the budget, what it
 * buys, and how much acquisition cost inflation the plan survives — and the deck can
 * read the same numbers instead of asserting its own.
 *
 * Model basis (FM-0013 assumptions.json, all sourced):
 *   round size       $350,000  (round_size)
 *   use of proceeds  Engineering 40% / Marketing & Sales 25% / Compliance & Security 20% / Team & Ops 10% / Reserve 5%
 *                    (uop_engineering_pct / uop_gtm_pct / uop_compliance_pct / uop_team_ops_pct)
 *   CAC              $350  (cac)
 *   binding CAC ceiling  $1020.5 — the 12-month payback test, which is
 *                    stricter than LTV/CAC >= 3x ($1111.08)
 *
 * Updated: 2026-10-12
 * ========================================================================== */

export interface ProceedsBucket {
  label: string;
  /** Share of the round, 0-1. The deck renders it as a percentage. */
  pct: number;
  /** The same share as an absolute amount in the round's currency. */
  amount: number;
}

/** Round size the split applies to (model input: round_size). */
export const PROCEEDS_ROUND = 350000;

/** Use of proceeds. The model asserts the sum is 1.0 (verify:capacity). */
export const PROCEEDS: ProceedsBucket[] = [
  { label: "Engineering", pct: 0.4, amount: 140000 },
  { label: "Marketing & Sales", pct: 0.25, amount: 87500 },
  { label: "Compliance & Security", pct: 0.2, amount: 70000 },
  { label: "Team & Ops", pct: 0.1, amount: 35000 },
  { label: "Reserve", pct: 0.05, amount: 17500 },
];

/** Allocation sum — 1.0 by construction; exported so the deck can display it. */
export const PROCEEDS_SUM = 1;

/**
 * What the go-to-market slice buys, and how much cost inflation the plan survives.
 * This is the model's answer to "isn't it too costly to go to market?": payback and
 * LTV/CAC are pure rearrangements of ARPU, gross margin and churn, so the ceiling is
 * derived, not asserted.
 */
export const GTM_BUDGET = 87500;
export const GTM_CUSTOMERS_THE_ROUND_BUYS = 250;
export const GTM_CAC_TODAY = 350;
export const GTM_CAC_CEILING = 1020.5;
export const GTM_CAC_HEADROOM_PCT = 1.9157;

/* ── OWNERSHIP (D-029) ─────────────────────────────────────────────────────────
 * Equity is an OUTCOME of the locked valuation, not an assumption: a cheque buys
 * ticket / post-money. The deck states the valuation and the resulting stake instead
 * of asserting a percentage, so a reader can size a cheque without asking.
 * All values are decimal fractions; the deck formats them.
 * ──────────────────────────────────────────────────────────────────────────── */
export const POST_MONEY_VALUATION = 7850000;
export const PRE_MONEY_VALUATION = 7500000;
export const ROUND_EQUITY_PCT = 0.0446;
export const TICKET_SIZE = 50000;
export const TICKET_EQUITY_PCT = 0.0064;
export const ROUND_MOIC = 7.0693;

/** The investor's IRR (FM-0013 D-101). Model output, never a remembered percentage — the ask
 *  slide prints a return, so the return has to come from the same place the multiple does. */
export const ROUND_IRR = 0.4787;

/**
 * WHAT ONE MINIMUM TICKET RETURNS — read from the model's own ladder rather than computed
 * here, so the deck cannot disagree with the workbook's equity tab. Exported so the ask slide
 * can state the deal from the investor's side without restating the valuation.
 */
export const MIN_TICKET_EXIT_PROCEEDS = 353463;
export const MIN_TICKET_MOIC = 7.0693;

/** The ticket FLOOR (FM-0013 D-041). It fixes the relationship count, and the relationship
 *  count is what decides how long the round takes, so the deck states it rather than a
 *  remembered "$25K minimum". */
export const MIN_TICKET_SIZE = 50000;

/**
 * The runway the round actually buys, measured FROM THE MONTH THE MONEY LANDS (FM-0013
 * D-041). The deck must never state a runway from memory: it used to say "18-month runway"
 * while the model measured 19 months from January — a different claim about a different
 * starting point, which is how a runway number stops meaning anything.
 */
export const ROUND_RUNWAY_MONTHS = 50;

/** The runway RULE the round is sized to, so the deck can say what it is aiming at. */
export const ROUND_TARGET_RUNWAY_MONTHS = 18;

/** The M12 milestone, derived from the ledger rather than typed into a slide. */
export const M12_CUSTOMERS = 1081;
export const M12_ARR = 875772;

/**
 * THE WEDGE LADDER — the GTM roadmap, sequenced by the model (see DATA.WEDGES). Stated as data
 * so the slide draws it rather than transcribing it, and month 1 is the first forecast year.
 */
export const WEDGES: Array<{ month: number; role: string; fte: number; why: string }> = [
  {
    "month": 1,
    "role": "Founder — outbound",
    "fte": 1,
    "why": "trading from the raise itself; the founder runs the fundraising wedge outbound (deck slide 13)"
  },
  {
    "month": 13,
    "role": "SDR / outbound",
    "fte": 1,
    "why": "lands with the Sales wedge — the second motion needs its own hands"
  },
  {
    "month": 19,
    "role": "SDR / outbound",
    "fte": 1,
    "why": "lands with the Recruiting wedge; the ladder is fully open from month 19"
  },
  {
    "month": 25,
    "role": "SDR / outbound",
    "fte": 1,
    "why": "carries the year-4 reachout volume"
  },
  {
    "month": 31,
    "role": "Sales lead",
    "fte": 1,
    "why": "a lead once there are hands to lead, not before"
  }
];

/**
 * THE SERVICE ROLLOUT — the roadmap the deck prints (see DATA.ROLLOUT). The quarter is derived from
 * the month and the model's own first forecast year, so the label and the month cannot disagree.
 */
export const ROLLOUT: Array<{ key: string; label: string; deliverable: string; month: number; quarter: string; incumbent: boolean }> = [
  {
    "key": "fundraising",
    "label": "Fundraising",
    "deliverable": "a qualified meeting held",
    "month": 10,
    "quarter": "Q4-26",
    "incumbent": true
  },
  {
    "key": "sales",
    "label": "Sales",
    "deliverable": "a qualified meeting held",
    "month": 13,
    "quarter": "Q1-27",
    "incumbent": false
  },
  {
    "key": "procurement",
    "label": "Procurement",
    "deliverable": "comparable offers received",
    "month": 16,
    "quarter": "Q2-27",
    "incumbent": false
  },
  {
    "key": "recruiting",
    "label": "Recruiting",
    "deliverable": "an interview held",
    "month": 19,
    "quarter": "Q3-27",
    "incumbent": false
  },
  {
    "key": "lender",
    "label": "Lender",
    "deliverable": "a meeting held",
    "month": 22,
    "quarter": "Q4-27",
    "incumbent": false
  },
  {
    "key": "partner",
    "label": "Partner",
    "deliverable": "a call held",
    "month": 25,
    "quarter": "Q1-28",
    "incumbent": false
  },
  {
    "key": "grantor",
    "label": "Grantor",
    "deliverable": "a programme meeting held",
    "month": 28,
    "quarter": "Q2-28",
    "incumbent": false
  },
  {
    "key": "journalist",
    "label": "Journalist",
    "deliverable": "a briefing held",
    "month": 31,
    "quarter": "Q3-28",
    "incumbent": false
  },
  {
    "key": "audience",
    "label": "Audience",
    "deliverable": "a signup",
    "month": 34,
    "quarter": "Q4-28",
    "incumbent": false
  }
];

/** The round calendar, anchored so the roadmap can point at the raise it belongs to. */
export const ROUND_MARKS: Array<{ id: string; name: string; opens: string; status: string }> = [
  {
    "id": "R1",
    "name": "Pre-seed round (this round)",
    "opens": "2026-10-01",
    "status": "open"
  },
  {
    "id": "R2",
    "name": "Seed round",
    "opens": "2027-06-01",
    "status": "planned"
  },
  {
    "id": "R3",
    "name": "Series A",
    "opens": "2029-01-15",
    "status": "indicative"
  }
];

/** Nine counterparts, ten motions — the roster, quoted from the manifest. */
export const ROSTER: Array<{ key: string; counterpart: string; index: string; motion: string; modelled: boolean }> = [
  {
    "key": "investor",
    "counterpart": "Investor",
    "index": "IIP",
    "motion": "Equity fundraising",
    "modelled": true
  },
  {
    "key": "customer",
    "counterpart": "Customer",
    "index": "ICP",
    "motion": "Sales · Support",
    "modelled": true
  },
  {
    "key": "lender",
    "counterpart": "Lender",
    "index": "ILP",
    "motion": "Debt / revenue-based",
    "modelled": true
  },
  {
    "key": "partner",
    "counterpart": "Partner",
    "index": "IPP",
    "motion": "Partnership / BD",
    "modelled": true
  },
  {
    "key": "hire",
    "counterpart": "Hire",
    "index": "IHP",
    "motion": "Recruiting",
    "modelled": true
  },
  {
    "key": "journalist",
    "counterpart": "Journalist",
    "index": "IJP",
    "motion": "Press / PR",
    "modelled": true
  },
  {
    "key": "vendor",
    "counterpart": "Vendor",
    "index": "IVP",
    "motion": "Procurement",
    "modelled": true
  },
  {
    "key": "grantor",
    "counterpart": "Grantor",
    "index": "IGP",
    "motion": "Grants / non-dilutive",
    "modelled": true
  },
  {
    "key": "audience",
    "counterpart": "Audience",
    "index": "IAP",
    "motion": "Marketing",
    "modelled": true
  }
];

/**
 * THE MODEL'S HEADLINE KPIs (see DATA.MODEL_KPIS). The deck prints a card per value; the labels
 * and the units are read here too, so a slide cannot pair a number with the wrong unit. The
 * unit is what the formatter needs; the deck never string-sniffs a value to pick a format.
 */
export const MODEL_KPIS: Record<
  'arr' | 'runRate' | 'revenue' | 'grossMargin' | 'ebitdaMargin' | 'nrr' | 'ltvCac' | 'irr' | 'moic' | 'breakevenMonth' | 'exitEv' | 'exitMultiple',
  { value: number; unit: 'usd' | 'pct' | 'x' | 'mo' }
> = {
  arr: { value: 7927679.97, unit: 'usd' },
  runRate: { value: 11037092.1, unit: 'usd' },
  revenue: { value: 21035009.53, unit: 'usd' },
  grossMargin: { value: 0.8826, unit: 'pct' },
  ebitdaMargin: { value: 0.4335, unit: 'pct' },
  nrr: { value: 1.08, unit: 'pct' },
  ltvCac: { value: 9.5236, unit: 'x' },
  irr: { value: 0.4787, unit: 'pct' },
  moic: { value: 7.0693, unit: 'x' },
  breakevenMonth: { value: 20, unit: 'mo' },
  exitEv: { value: 55493759.78, unit: 'usd' },
  exitMultiple: { value: 7, unit: 'x' },
};

/**
 * NOTE, so the next reader does not "fix" it: the two chips on the model slide
 * ("$50K MRR by month 12", "$1.2M ARR by month 24") are NOT wired to the model, and that is
 * deliberate (D-084). They are published COMMITMENTS — a floor we have promised — while the
 * model's M12/M24 figures are the PLAN. The plan beats both ($70.9K MRR, $2.21M ARR), and
 * raising a published floor to the plan's number would be promising more than we have to.
 * The comparison is reported where it belongs: the workbook's Cover and the promise ledger.
 */

/**
 * The horizon ROUND_MOIC compounds over (model input: duration_years). Exported so the
 * deck can state a return PER YEAR without hardcoding the period — the multiple, the
 * exit it rests on and the years between them cannot drift apart.
 */
export const EXIT_HORIZON_YEARS = 5;

/**
 * The exit this price rests on (D-031): exit EV = year-5 ending ARR × this multiple.
 *
 * BASED ON THE YEAR-END RUN-RATE (ARR), not on the revenue earned across the year — that is
 * what an acquisition of a subscription business is priced on, so the comparable quantity is
 * EV/Sales for listed software. SOURCED (FM-0013 D-039): Damodaran, Revenue Multiples by
 * Sector (United States), data as of January 2026 — Software (System & Application) EV/Sales
 * 11.41, Software (Internet) 9.56, Software (Entertainment) 9.13.
 *
 * 7x is therefore a DELIBERATE DISCOUNT of about 39% to the system-software comparable,
 * applied because a small illiquid private company does not command a listed multiple. Two
 * facts keep it on the conservative side: the comps are TRAILING sales while ours is a
 * forward run-rate (so the like-for-like figure would be higher, not lower), and the
 * multiple is applied years out when a company of this size is far below the listed median.
 *
 * The band is 6x-11.4x, and the price in force needs 7.52x to return the target —
 * inside that band. At the low end the deal does not clear its target, which the deck
 * states rather than hides.
 */
export const EXIT_EV_MULTIPLE = 7;

/** The sourced band around the assumed multiple (FM-0013 D-039) — carried so the deck
 *  cannot quote a point estimate as if it were the only possibility. */
export const EXIT_EV_MULTIPLE_BAND = { low: 6, high: 11.4 } as const;

/** The multiple at which this price stops returning the target (FM-0013 D-038/D-039). */
export const EXIT_MULTIPLE_REQUIRED = 7.52;
